import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Search, AlertTriangle, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

interface Incident {
  id: number;
  reference: string;
  title: string;
  description: string;
  type: string;
  severity: string;
  status: string;
  reported_at: string;
  reported_by: string;
  assigned_to: string;
  assigned_to_name: string;
  resolution: string;
  resolved_at: string;
  is_anonymous: boolean;
  actions: IncidentAction[];
  timeline: TimelineEntry[];
  created_at: string;
}

interface IncidentAction {
  id: number;
  description: string;
  assigned_to: string;
  due_date: string;
  status: string;
  completed_at: string;
}

interface TimelineEntry {
  id: number;
  event: string;
  details: string;
  created_at: string;
  user_name: string;
}

interface DashboardStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  overdue_actions: number;
}

const severityColors: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const statusColors: Record<string, string> = {
  reported: 'bg-gray-100 text-gray-800',
  investigating: 'bg-blue-100 text-blue-800',
  containment: 'bg-yellow-100 text-yellow-800',
  remediation: 'bg-orange-100 text-orange-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-slate-100 text-slate-800',
};

const actionStatusColors: Record<string, string> = {
  open: 'bg-red-100 text-red-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  overdue: 'bg-red-200 text-red-900',
};

const INCIDENT_TYPES = ['security', 'data_breach', 'regulatory', 'whistleblowing', 'health_safety', 'environmental', 'operational', 'other'];
const SEVERITIES = ['low', 'medium', 'high', 'critical'];
const STATUSES = ['reported', 'investigating', 'containment', 'remediation', 'resolved', 'closed'];

const EMPTY_FORM = {
  title: '',
  description: '',
  type: 'security',
  severity: 'medium',
  is_anonymous: false,
};

const IncidentManagement: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (typeFilter) params.set('type', typeFilter);
      if (severityFilter) params.set('severity', severityFilter);
      if (statusFilter) params.set('status', statusFilter);

      const [listRes, statsRes] = await Promise.all([
        fetch(`/api/compliance/incidents?${params}`, { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/compliance/incidents/dashboard', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setIncidents(listRes.incidents || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [search, typeFilter, severityFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/incidents', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to create incident');
      setCreateModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnonymousReport = () => {
    setForm({ ...EMPTY_FORM, is_anonymous: true });
    setCreateModal(true);
  };

  const toggleExpand = (id: number) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-7 h-7 text-orange-600" />
          <h1 className="text-2xl font-bold text-gray-900">Incident Management</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={handleAnonymousReport} className="flex items-center gap-1 px-3 py-2 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100">
            Anonymous Report
          </button>
          <button onClick={() => { setForm(EMPTY_FORM); setCreateModal(true); }} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Report Incident
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Incidents', value: stats.total, color: 'text-gray-900' },
            { label: 'Reported', value: stats.by_status?.reported || 0, color: 'text-gray-600' },
            { label: 'Investigating', value: stats.by_status?.investigating || 0, color: 'text-blue-600' },
            { label: 'Open (Active)', value: (stats.by_status?.reported || 0) + (stats.by_status?.investigating || 0) + (stats.by_status?.containment || 0) + (stats.by_status?.remediation || 0), color: 'text-orange-600' },
            { label: 'Overdue Actions', value: stats.overdue_actions, color: 'text-red-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Status workflow visualization */}
      <div className="bg-white border rounded-xl p-4">
        <p className="text-xs text-gray-500 mb-2 font-medium uppercase">Workflow</p>
        <div className="flex items-center gap-1 text-xs overflow-x-auto">
          {STATUSES.map((s, i) => (
            <React.Fragment key={s}>
              <span className={`px-3 py-1.5 rounded-full font-medium capitalize whitespace-nowrap ${statusColors[s]}`}>
                {s} {stats?.by_status?.[s] ? `(${stats.by_status[s]})` : ''}
              </span>
              {i < STATUSES.length - 1 && <span className="text-gray-300 mx-1">&#8594;</span>}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search incidents..." value={search} onChange={e => setSearch(e.target.value)} className="border rounded-lg pl-9 pr-3 py-2 text-sm w-full" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Types</option>
          {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Severities</option>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : incidents.length === 0 ? (
          <div className="py-12 text-gray-500">No incidents found.</div>
        ) : (
          <div className="divide-y">
            {incidents.map(inc => (
              <div key={inc.id}>
                <div className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(inc.id)}>
                  <span className="text-gray-400 mr-3">
                    {expandedId === inc.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">{inc.reference}</span>
                      <span className="font-medium text-gray-900 truncate">{inc.title}</span>
                      {inc.is_anonymous && <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Anonymous</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-4 shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${severityColors[inc.severity] || 'bg-gray-100 text-gray-700'}`}>
                      {inc.severity}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[inc.status] || 'bg-gray-100 text-gray-700'}`}>
                      {inc.status}
                    </span>
                    <span className="text-xs text-gray-500 capitalize">{inc.type.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-400">{new Date(inc.reported_at || inc.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {expandedId === inc.id && (
                  <div className="bg-gray-50 px-12 py-4 space-y-4 border-t">
                    {inc.description && <p className="text-sm text-gray-700">{inc.description}</p>}
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Reported By</p>
                        <p className="font-medium">{inc.is_anonymous ? 'Anonymous' : (inc.reported_by || '—')}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Assigned To</p>
                        <p className="font-medium">{inc.assigned_to_name || inc.assigned_to || '—'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Reported</p>
                        <p className="font-medium">{inc.reported_at ? new Date(inc.reported_at).toLocaleString() : '—'}</p>
                      </div>
                    </div>

                    {/* Actions sub-table */}
                    {inc.actions && inc.actions.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Actions</h4>
                        <table className="w-full text-sm">
                          <thead className="bg-white">
                            <tr>
                              {['Description', 'Assigned To', 'Due Date', 'Status'].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {inc.actions.map(a => {
                              const overdue = a.due_date && new Date(a.due_date) < new Date() && a.status !== 'completed';
                              return (
                                <tr key={a.id} className={overdue ? 'bg-red-50/50' : ''}>
                                  <td className="px-3 py-2 text-left text-gray-700">{a.description}</td>
                                  <td className="px-3 py-2 text-left text-gray-600">{a.assigned_to || '—'}</td>
                                  <td className="px-3 py-2 text-left text-gray-600">
                                    {a.due_date ? new Date(a.due_date).toLocaleDateString() : '—'}
                                    {overdue && <span className="ml-1 text-xs text-red-600 font-medium">OVERDUE</span>}
                                  </td>
                                  <td className="px-3 py-2 text-left">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${actionStatusColors[overdue ? 'overdue' : a.status] || 'bg-gray-100 text-gray-700'}`}>
                                      {a.status.replace(/_/g, ' ')}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Timeline */}
                    {inc.timeline && inc.timeline.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Timeline</h4>
                        <div className="space-y-2">
                          {inc.timeline.map(t => (
                            <div key={t.id} className="flex items-start gap-3">
                              <Clock size={14} className="text-gray-400 mt-0.5 shrink-0" />
                              <div>
                                <p className="text-sm text-gray-700">{t.event}{t.details ? `: ${t.details}` : ''}</p>
                                <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleString()} {t.user_name ? `by ${t.user_name}` : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {form.is_anonymous ? 'Anonymous Incident Report' : 'Report Incident'}
              </h2>
              {form.is_anonymous && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg text-sm">
                  This report will be submitted anonymously. Your identity will not be recorded.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Describe the incident in detail..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
                    {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
                <button onClick={handleCreate} disabled={submitting || !form.title.trim()} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentManagement;
